import React from "react";
import { Link } from "react-router-dom";

/**
 * Public methodology page — reproduces the PRD's public-facing methodology
 * summary. No auth required. Content is intentionally verbatim from PRD §28
 * so any legal / customer-support reference matches source-of-truth.
 */
export default function MethodologyPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto max-w-4xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-slate-900 text-white text-xs font-bold tracking-widest">
              RA
            </span>
            <span className="text-sm font-semibold">RecoveryAssure</span>
          </Link>
          <Link
            to="/login"
            className="text-sm text-slate-700 hover:text-slate-900 underline"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12 space-y-10">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Methodology
          </div>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">
            How RecoveryAssure decides whether recovery is feasible
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            RecoveryAssure separates deterministic engine truth from AI
            interpretation. Every finding is code-defined, every derived value
            traces back to source data or a named rule, and no LLM is allowed
            to change severities, RTOs, MTPDs, MBCOs, or triggered rules.
          </p>
        </div>

        <section aria-labelledby="pipeline">
          <h2 id="pipeline" className="text-xl font-semibold">
            The pipeline, end to end
          </h2>
          <ol className="mt-4 space-y-6 border-l border-slate-200 pl-6">
            <li>
              <div className="text-sm font-semibold text-slate-900">
                1. Source data
              </div>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                Business Impact Analyses, application catalogues, supplier
                registers, and DR test records enter the system as XLSX or CSV
                files. Nothing is executed — we open workbooks in read-only,
                data-only mode. Macros, embedded objects, and OLE content are
                rejected before parsing.
              </p>
            </li>
            <li>
              <div className="text-sm font-semibold text-slate-900">
                2. Deterministic normalisation
              </div>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                Numbers, durations, percentages, and enums are normalised by
                explicit rules. Ambiguous phrases like "1 business day" or
                "EOD" are surfaced as errors rather than silently interpreted.
                Every canonical value carries a Provenance record tying it back
                to the source file, sheet, row, and column.
              </p>
            </li>
            <li>
              <div className="text-sm font-semibold text-slate-900">
                3. Rule engine
              </div>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                Twenty deterministic rules evaluate the canonical dataset —
                each rule pins its version, its config, and the facts it used.
                Findings are code-defined; LLMs do not decide feasibility,
                severities, or triggered rules.
              </p>
            </li>
            <li>
              <div className="text-sm font-semibold text-slate-900">
                4. Recovery feasibility
              </div>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                For each process we evaluate nine dimensions — TIME, TECHNOLOGY,
                DATA, CAPACITY, PEOPLE, SUPPLIERS, LOCATION, DEPENDENCIES,
                VALIDATION — and derive an overall status: FEASIBLE, AT_RISK,
                NOT_FEASIBLE, or INSUFFICIENT_DATA. Confidence in the conclusion
                is a separate axis and is downgraded when data is stale, tests
                are missing, or duplicate patterns weaken evidence.
              </p>
            </li>
            <li>
              <div className="text-sm font-semibold text-slate-900">
                5. AI assistance ·{" "}
                <span className="rounded bg-emerald-50 px-2 py-0.5 text-[11px] uppercase text-emerald-800 tracking-widest align-middle">
                  Available today
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                AI assistance is on-demand and interpretive only. The
                deterministic rule engine remains authoritative for every
                finding, feasibility status, severity, calculation, and any
                RTO / RPO / MTPD / MBCO value. The LLM cannot determine or
                change any of those; it is never on the code path that
                derives them.
              </p>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Every AI output is labelled as{" "}
                <span className="font-medium">AI-generated (INFERRED /
                RECOMMENDED)</span>{" "}
                and requires professional review before being acted on.
                Quantitative claims returned by the model are
                guardrail-validated against the deterministic context — if a
                fact contradicts the underlying{" "}
                <span className="font-medium">SOURCE FACT</span> or{" "}
                <span className="font-medium">CALCULATED</span> value, the AI
                narrative is withheld and the user sees the deterministic
                finding on its own rather than a fabricated one. When the AI
                provider is unavailable (LLM_NOT_CONFIGURED /
                LLM_PROVIDER_UNAVAILABLE), the deterministic analysis remains
                fully usable — engine output, findings, feasibility, and PDF
                reports are unaffected.
              </p>
            </li>
            <li>
              <div className="text-sm font-semibold text-slate-900">
                6. Human review
              </div>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                Every finding, every dimension result, every derivation trace
                is retained and exportable. Historical runs are immutable — a
                run's rule library snapshot, mapping profile snapshot, and
                dataset are pinned by content hash so the same inputs always
                produce the same output.
              </p>
            </li>
          </ol>
        </section>

        <section aria-labelledby="disclaimer">
          <h2 id="disclaimer" className="text-xl font-semibold">
            Disclaimer
          </h2>
          <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 leading-relaxed">
            RecoveryAssure produces analytical findings based on the data you
            provide. Findings are not a substitute for professional business
            continuity or risk-management advice. Your organisation remains
            responsible for its recovery strategy and regulatory obligations.
            RecoveryAssure does not guarantee that any process, system, or
            dependency will meet its recovery objectives.
          </div>
        </section>

        <div className="pt-6 border-t border-slate-200 text-xs text-slate-500">
          Return to{" "}
          <Link to="/login" className="underline text-slate-700">
            sign in
          </Link>
          .
        </div>
      </main>
    </div>
  );
}
